import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Clock, TrendingDown, Zap, Shield } from 'lucide-react';

const benefits = [
  {
    icon: Clock,
    stat: '10h+',
    label: 'récupérées par semaine',
    description: 'Saisies, relances, prises de rendez-vous : les tâches répétitives grignotent vos journées. L\'IA les prend en charge pour vous.',
  },
  {
    icon: TrendingDown,
    stat: '-30%',
    label: 'de coûts opérationnels',
    description: 'Doublons, erreurs de saisie et allers-retours manuels coûtent cher, sans que vous les voyiez. L\'automatisation les élimine à la source.',
  },
  {
    icon: Zap,
    stat: '< 1 min',
    label: 'pour répondre à un client',
    description: 'Un client qui attend une réponse trop longtemps va voir ailleurs. Répondez instantanément, même en plein rush.',
  },
  {
    icon: Shield,
    stat: '24/7',
    label: 'sans interruption',
    description: 'Appels manqués le soir, messages sans réponse le week-end : chaque absence est une vente perdue. L\'IA, elle, ne dort jamais.',
  },
];

function BenefitCard({ benefit, index }) {
  const ref = useScrollReveal();
  const Icon = benefit.icon;

  return (
    <div
      ref={ref}
      data-testid={`benefit-card-${index}`}
      className="scroll-reveal benefit-card p-8 rounded-3xl border border-border/50 bg-card hover:bg-secondary/30"
      style={{ transitionDelay: `${index * 0.12}s` }}
    >
      <div className="w-12 h-12 rounded-2xl bg-[hsl(211,100%,50%)]/10 dark:bg-[hsl(211,100%,55%)]/10 flex items-center justify-center mb-6">
        <Icon className="w-6 h-6 text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)]" />
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
          {benefit.stat}
        </span>
        <span className="text-sm font-medium text-muted-foreground">
          {benefit.label}
        </span>
      </div>

      <p className="text-sm text-muted-foreground leading-relaxed mt-3">
        {benefit.description}
      </p>
    </div>
  );
}

export default function WhySection() {
  const titleRef = useScrollReveal();

  return (
    <section id="avantages" data-testid="why-section" className="py-24 md:py-32 bg-secondary/30">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div ref={titleRef} className="scroll-reveal mb-16 md:mb-20">
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[hsl(211,100%,50%)] dark:text-[hsl(211,100%,55%)] mb-4">
            Pourquoi Fast Task
          </p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-foreground mb-6 max-w-3xl">
            L'IA au service de votre croissance
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Moins de tâches répétitives, plus de temps pour innover.
            Nos solutions sont conçues pour transformer votre quotidien.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {benefits.map((benefit, i) => (
            <BenefitCard key={i} benefit={benefit} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
